import { CardActions } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import { ComputedDatum, ResponsivePie, PieSvgProps } from '@nivo/pie';
import React from 'react';

import { GraphDatum } from './_type';
import LegendItem from './legend-item';
import Overlay, { OverlayProps } from './overlay';
import Tooltip from './tooltip';

import Arranger from '~c/components/base/arranger';
import Card from '~c/components/base/card';
import { DesignToken } from '~c/components/base/core';
import { spc } from '~c/components/base/spacer';
import { useWindowSize } from '~c/hooks/use-hooks';

export type { GraphDatum } from './_type';

const DEFAULT_DONUT_INNER_RADIUS = 0.6;
const MIN_GRAPH_HEIGHT = 184;
// Special data entry used when all data values are 0
const EMPTY_VALUE_DATA = [
  { id: 'empty-value-id', label: '', color: DesignToken.textMuted, value: 1 },
];

interface Props extends Omit<PieSvgProps<GraphDatum>, 'width' | 'height'> {
  className?: string;
  title?: string;
  subHeader?: React.ReactNode;
  /**
   * Specify if rendering a donut piechart
   */
  donutOpt?: OverlayProps;
  /**
   * Callback when a pie slice is clicked. Receives the datum and the
   * native mouse event so callers can position a popover at the click point.
   */
  onSliceClick?: (
    datum: GraphDatum,
    event: React.MouseEvent<SVGPathElement>
  ) => void;
}

const PieGraph: React.FC<Props> = ({
  className,
  data,
  title,
  subHeader,
  donutOpt,
  onSliceClick,
  ...props
}: Props) => {
  const { height } = useWindowSize();
  const pieHeight = Math.max(MIN_GRAPH_HEIGHT, height / 5);
  // If none of the data entry contains a value, we want
  // to render a grey circle
  const noValue = data.every(({ value }) => !value);

  return (
    <div className={className}>
      <Card title={title} subHeader={subHeader}>
        <Arranger
          className={`pie-graph${onSliceClick ? ' pie-graph--clickable' : ''}`}
          alignItems="center"
          justify="center"
          sx={{
            height: pieHeight,
            ...(onSliceClick &&
              !noValue && { '& path': { cursor: 'pointer' } }),
          }}
        >
          <ResponsivePie
            data={noValue ? EMPTY_VALUE_DATA : data}
            {...(!!donutOpt && {
              innerRadius: DEFAULT_DONUT_INNER_RADIUS,
              padAngle: 0.7,
              cornerRadius: 0,
            })}
            animate
            enableArcLinkLabels={false}
            enableArcLabels={false}
            borderWidth={0}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.2]],
            }}
            colors={{ datum: 'data.color' }}
            tooltip={({ datum }) => <Tooltip {...datum} />}
            {...(onSliceClick &&
              !noValue && {
                onClick: (
                  node: ComputedDatum<GraphDatum>,
                  event: React.MouseEvent<SVGPathElement>
                ) => {
                  onSliceClick(node.data, event);
                },
                activeOuterRadiusOffset: 4,
              })}
            {...props}
          />
          {!!donutOpt && (
            <Overlay
              // This makes sure the overlay is completely within the
              // donut inner circle
              width={pieHeight * (DEFAULT_DONUT_INNER_RADIUS - 0.1)}
              {...donutOpt}
            />
          )}
        </Arranger>
        <CardActions className="legend-container">
          <Arranger justify="space-around">
            {data.map((entry, idx) => (
              <LegendItem key={idx} {...entry} />
            ))}
          </Arranger>
        </CardActions>
      </Card>
    </div>
  );
};

export default muiStyled(PieGraph)(({ theme }) => {
  return css`
    .legend-container {
      padding: ${theme.spacing(spc.m)} 0 0 0;

      /* The legend bar take up full width */
      > div {
        flex-grow: 1;
      }
    }
  `;
});
